import { uniqBy } from 'lodash-es'
import type { Bundle } from '@/features/admin/bundle/Bundle'
import {
  type InventoryItem,
  type InventoryItemPricing,
  InventoryItemExtensionTypeSpecification,
  InventoryItemTaxTypeSpecification,
} from '@/features/inventory/InventoryItem'
import { Id } from '@/infra/id'
import type { ComputedInventoryItem } from './legend-computation'

/**
 * A service item on the previewed job.
 */
export interface PricedInventoryItem {
  id: string
  name: string
  shortId: string
  quantity: number
  /**
   * How much of the quantity the client is not charged for.
   */
  courtesyQuantity: number
  courtesyMultiplier: number | null
}

/**
 * The prep and review flow's settings that pricing depends on.
 */
export interface PrepAndReviewPricingConfig {
  enabled: boolean
  reviewServiceItemId: string
  prepAndReviewServiceItemId: string
}

/**
 * The previewed job's service items and prices.
 */
export interface PreviewPricing {
  inventoryItems: PricedInventoryItem[]
  bundleName: string | null
  price: number
  proPrice: number
  /**
   * The review item is on the job, so tax items are priced by their review
   * prices.
   */
  usesReviewPricing: boolean
}

/**
 * Everything pricing depends on.
 */
export interface PreviewPricingInput {
  /**
   * The service items the engine put on the job.
   */
  computedItems: readonly ComputedInventoryItem[]
  /**
   * The service items the legend can use.
   */
  inventoryItems: readonly InventoryItem[]
  bundles: readonly Bundle[]
  prepAndReviewConfig: PrepAndReviewPricingConfig
}

/**
 * The most of a service item a job can have.
 */
const maxQuantity = 20_000

/**
 * Prices and rates have 5 decimals in the API.
 */
const scale = 100_000

/**
 * The service items the engine put on the job that the legend can't use,
 * which can't be priced.
 *
 * @param computedItems
 * @param inventoryItems
 */
export function findUnknownInventoryItemIds(
  computedItems: readonly ComputedInventoryItem[],
  inventoryItems: readonly InventoryItem[],
) {
  return computedItems
    .map((item) => item.id)
    .filter((id) => !inventoryItems.some((i) => i.id === id))
}

/**
 * Prices the job the engine computed the way the job is billed: in cents, one
 * item at a time, with markups and markdowns rounded half to even against the
 * total of the items before them. Throws when a service item is unknown; see
 * {@link findUnknownInventoryItemIds}.
 *
 * @param input
 */
export function computePricing(input: PreviewPricingInput): PreviewPricing {
  const { inventoryItems, prepAndReviewConfig } = input
  // The job gets one of each service item, priced by the engine's first entry.
  const computedItems = uniqBy(input.computedItems, (item) => item.id)
  const ids = computedItems.map((item) => item.id)
  const bundle = mostRelevantBundle(input.bundles, ids)
  const usesReviewPricing =
    prepAndReviewConfig.enabled &&
    ids.includes(prepAndReviewConfig.reviewServiceItemId)

  const includedItems = bundle ? getIncludedItems(bundle) : []
  const jobItems = computedItems.map((computed): JobItem => {
    const inventoryItem = inventoryItems.find((i) => i.id === computed.id)
    if (!inventoryItem) {
      throw new Error(`Unknown service item ${computed.id}`)
    }
    const included = includedItems.find(
      (i) => i.inventoryItemId === computed.id,
    )
    const multiplier = computed.courtesyMultiplier ?? 1
    const quantity = Math.min(computed.quantity, maxQuantity)
    return {
      computed,
      inventoryItem,
      quantity,
      // The bundle price covers the base item.
      courtesyQuantity:
        bundle?.baseItem.inventoryItemId === computed.id
          ? quantity
          : (included?.includedQuantity ?? inventoryItem.courtesyAmount) *
            multiplier,
    }
  })

  const totals = new JobTotals(usesReviewPricing)
  const baseItem = bundle
    ? jobItems.find((i) => i.computed.id === bundle.baseItem.inventoryItemId)
    : undefined
  if (bundle && baseItem) {
    totals.addItem(baseItem)
    totals.addLine(toCents(bundle.price), 0, baseItem.quantity)
  }
  orderByPriceType(jobItems.filter((item) => item !== baseItem)).forEach(
    (item) => totals.addItem(item),
  )

  return {
    inventoryItems: (baseItem
      ? [baseItem, ...jobItems.filter((item) => item !== baseItem)]
      : jobItems
    ).map(toPricedInventoryItem),
    bundleName: bundle?.name ?? null,
    price: totals.price / 100,
    proPrice: totals.proPrice / 100,
    usesReviewPricing,
  }
}

interface JobItem {
  computed: ComputedInventoryItem
  inventoryItem: InventoryItem
  quantity: number
  courtesyQuantity: number
}

/**
 * The job's running client price and pro payout, in cents. Courtesy quantity
 * is free for the client, but the pro is still paid for it.
 */
class JobTotals {
  price = 0
  proPrice = 0

  constructor(private readonly _usesReviewPricing: boolean) {}

  addItem({ inventoryItem, quantity, courtesyQuantity }: JobItem) {
    this.addLine(
      this._unitPrice(inventoryItem),
      this._unitProPrice(inventoryItem),
      quantity,
      Math.max(0, quantity - courtesyQuantity),
    )
  }

  addLine(
    unitPrice: number,
    unitProPrice: number,
    quantity: number,
    chargedQuantity = quantity,
  ) {
    this.price += unitPrice * chargedQuantity
    this.proPrice += unitProPrice * quantity
  }

  private _unitPrice(item: InventoryItem) {
    const spec = item.typeSpecification
    if (
      spec instanceof InventoryItemTaxTypeSpecification &&
      this._usesReviewPricing
    ) {
      return toCents(spec.reviewPrice)
    }
    if (spec instanceof InventoryItemExtensionTypeSpecification) {
      return getExtensionFlatFee(spec, 'clientChargeAmount')
    }
    switch (item.priceType) {
      case 'Flat':
      case 'Fee':
        return toCents(item.price)
      case 'Markup':
        return getRateDifference(this.price, toRate(item.price))
      case 'Markdown':
        return getRateDifference(this.price, -toRate(item.price))
    }
  }

  private _unitProPrice(item: InventoryItem) {
    const spec = item.typeSpecification
    if (
      spec instanceof InventoryItemTaxTypeSpecification &&
      this._usesReviewPricing
    ) {
      return toCents(spec.reviewPayout)
    }
    if (spec instanceof InventoryItemExtensionTypeSpecification) {
      return getExtensionFlatFee(spec, 'proPayoutAmount')
    }
    switch (item.priceType) {
      case 'Fee':
        return 0
      case 'Flat':
        return toCents(item.proPrice)
      case 'Markup':
        return getRateDifference(this.proPrice, toRate(item.proPrice))
      case 'Markdown':
        return getRateDifference(this.proPrice, -toRate(item.proPrice))
    }
  }
}

/**
 * The bundle that applies to the job: one whose base item is on the job, with
 * the most of its included items on the job. Ties go to the cheapest, then
 * to the first.
 *
 * @param bundles
 * @param inventoryItemIds The IDs of the service items on the job.
 */
export function mostRelevantBundle(
  bundles: readonly Bundle[],
  inventoryItemIds: readonly string[],
): Bundle | null {
  const matching = bundles.filter((bundle) => {
    const baseItemId = bundle.baseItem.inventoryItemId
    return Id.some(baseItemId) && inventoryItemIds.includes(baseItemId)
  })
  if (matching.length === 0) {
    return null
  }

  const counts = new Map(
    matching.map((bundle) => [
      bundle,
      getIncludedItems(bundle).filter(
        (item) =>
          Id.some(item.inventoryItemId) &&
          inventoryItemIds.includes(item.inventoryItemId),
      ).length,
    ]),
  )
  const mostIncluded = Math.max(...counts.values())
  const candidates =
    mostIncluded === 0
      ? matching
      : matching.filter((bundle) => counts.get(bundle) === mostIncluded)

  return candidates.reduce((cheapest, bundle) =>
    bundle.price < cheapest.price ? bundle : cheapest,
  )
}

/**
 * The bundle's included items, without its base item, which the model lists
 * among them.
 *
 * @param bundle
 */
export function getIncludedItems(bundle: Bundle) {
  return bundle.includedItems.filter((item) => !item.isBaseItem)
}

/**
 * Percentage fees are a share of what is still owed, and progression fees
 * depend on the job's progress, neither of which a new job has.
 *
 * @param spec
 * @param amount
 */
function getExtensionFlatFee(
  spec: InventoryItemExtensionTypeSpecification,
  amount: 'clientChargeAmount' | 'proPayoutAmount',
) {
  return spec.billingInfo.type === 'Regular' && spec.billingType === 'FlatFee'
    ? toCents(spec.billingInfo[amount])
    : 0
}

/**
 * The total marked up (or down, for a negative rate), rounded, less the
 * total. That isn't always the rounded share of the total: 15% of $10.05
 * marks it up by $1.51, not $1.50.
 *
 * @param totalCents
 * @param rate A fraction scaled by {@link scale}.
 */
function getRateDifference(totalCents: number, rate: number) {
  return (
    divideRoundingHalfToEven(totalCents * (scale + rate), scale) - totalCents
  )
}

/**
 * The model holds rates as percentages, which can be a float's width off the
 * API's 5 decimals (2.9 is stored as 2.9000000000000004).
 *
 * @param percentage
 */
function toRate(percentage: number) {
  return Math.round(percentage * (scale / 100))
}

/**
 * Rounds an amount to cents, half to even, the way billing rounds every
 * amount.
 *
 * @param amount
 */
function toCents(amount: number) {
  return divideRoundingHalfToEven(Math.round(amount * scale), scale / 100)
}

function divideRoundingHalfToEven(dividend: number, divisor: number): number {
  if (dividend < 0) {
    return -divideRoundingHalfToEven(-dividend, divisor)
  }
  // BigInt keeps the products of large totals and rates exact.
  const n = BigInt(dividend)
  const d = BigInt(divisor)
  const quotient = n / d
  const twiceRemainder = (n % d) * 2n
  const roundsUp =
    twiceRemainder > d || (twiceRemainder === d && quotient % 2n === 1n)
  return Number(roundsUp ? quotient + 1n : quotient)
}

/**
 * Flat items apply first, then markups and markdowns of the running total,
 * then fees.
 *
 * @param items
 */
function orderByPriceType(items: readonly JobItem[]) {
  const ofType = (type: InventoryItemPricing) =>
    items.filter((i) => i.inventoryItem.priceType === type)
  return [
    ...ofType('Flat'),
    ...ofType('Markup'),
    ...ofType('Markdown'),
    ...ofType('Fee'),
  ]
}

function toPricedInventoryItem({
  computed,
  inventoryItem,
  quantity,
  courtesyQuantity,
}: JobItem): PricedInventoryItem {
  return {
    id: computed.id,
    name: inventoryItem.name,
    shortId: inventoryItem.shortId,
    quantity,
    courtesyQuantity,
    courtesyMultiplier: computed.courtesyMultiplier,
  }
}
